import * as React from "react";
import { StandardProps, PropTypes as MuiPropTypes } from "@material-ui/core";
import { ButtonBaseProps } from "@material-ui/core/ButtonBase";
import MuiButton, { ButtonProps as MuiButtonProps, ButtonClassKey as MuiButtonClassKey } from "@material-ui/core/Button";
import { PropTypes } from "../index";
import { withStyles, createStyles, Theme, StyleRulesCallback } from './styles';
import { fade } from '@material-ui/core/styles/colorManipulator';

export interface ButtonProps extends StandardProps<ButtonBaseProps, ButtonClassKey, 'component'> {
    color?: PropTypes.Color;
    component?: React.ReactType<ButtonProps>;
    disabled?: boolean;
    disableFocusRipple?: boolean;
    disableRipple?: boolean;
    fullWidth?: boolean;
    href?: string;
    mini?: boolean;
    size?: 'small' | 'medium' | 'large';
    type?: 'button' | 'submit' | 'reset';
    variant?: 'text' | 'flat' | 'outlined' | 'contained' | 'raised' | 'fab' | 'extendedFab';
    textTransform?: 'initial' | 'uppercase' | 'lowercase' | 'capitalize';
}

export type ButtonClassKey =//= MuiButtonClassKey

    | 'root'
    | 'label'

    | 'textSuccess'
    | 'outlinedSuccess'
    | 'containedSuccess'

    | 'textInfo'
    | 'outlinedInfo'
    | 'containedInfo'

    | 'textWarning'
    | 'outlinedWarning'
    | 'containedWarning'

    | 'textDanger'
    | 'outlinedDanger'
    | 'containedDanger'

    | 'rootUppercase'
    | 'rootLowercase'
    | 'rootCapitalize'
    ;



const styles = ({ palette }: Theme) => {

    return createStyles({
        root: {
            textTransform: 'initial'
        },
        label: {},
        rootUppercase: {
            textTransform: 'uppercase'
        },
        rootLowercase: {
            textTransform: 'lowercase'
        },
        rootCapitalize: {
            textTransform: 'capitalize'
        },
        disabled: {},
        textSuccess: {
            color: palette.success.main,
            '&:hover': {
                backgroundColor: fade(palette.success.main, palette.action.hoverOpacity),
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
        },
        outlinedSuccess: {
            color: palette.success.main,
            border: `1px solid ${fade(palette.success.main, 0.5)}`,
            '&:hover': {
                border: `1px solid ${palette.success.main}`,
                backgroundColor: fade(palette.success.main, palette.action.hoverOpacity),
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
            '&$disabled': {
                border: `1px solid ${palette.action.disabled}`,
            },
        },
        containedSuccess: {
            color: palette.success.contrastText,
            backgroundColor: palette.success.main,
            '&:hover': {
                backgroundColor: palette.success.dark,
                '@media (hover: none)': {
                    backgroundColor: palette.success.main,
                },
            },
        },


        textWarning: {
            color: palette.warning.main,
            '&:hover': {
                backgroundColor: fade(palette.warning.main, palette.action.hoverOpacity),
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
        },
        outlinedWarning: {
            color: palette.warning.main,
            border: `1px solid ${fade(palette.warning.main, 0.5)}`,
            '&:hover': {
                border: `1px solid ${palette.warning.main}`,
                backgroundColor: fade(palette.warning.main, palette.action.hoverOpacity),
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
            '&$disabled': {
                border: `1px solid ${palette.action.disabled}`,
            },
        },
        containedWarning: {
            color: palette.warning.contrastText,
            backgroundColor: palette.warning.main,
            '&:hover': {
                backgroundColor: palette.warning.dark,
                '@media (hover: none)': {
                    backgroundColor: palette.warning.main,
                },
            },
        },


        textInfo: {
            color: palette.info.main,
            '&:hover': {
                backgroundColor: fade(palette.info.main, palette.action.hoverOpacity),
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
        },
        outlinedInfo: {
            color: palette.info.main,
            border: `1px solid ${fade(palette.info.main, 0.5)}`,
            '&:hover': {
                border: `1px solid ${palette.info.main}`,
                backgroundColor: fade(palette.info.main, palette.action.hoverOpacity),
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
            '&$disabled': {
                border: `1px solid ${palette.action.disabled}`,
            },
        },
        containedInfo: {
            color: palette.info.contrastText,
            backgroundColor: palette.info.main,
            '&:hover': {
                backgroundColor: palette.info.dark,
                '@media (hover: none)': {
                    backgroundColor: palette.info.main,
                },
            },
        },

        textDanger: {
            color: palette.danger.main,
            '&:hover': {
                backgroundColor: fade(palette.danger.main, palette.action.hoverOpacity),
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
        },
        outlinedDanger: {
            color: palette.danger.main,
            border: `1px solid ${fade(palette.danger.main, 0.5)}`,
            '&:hover': {
                border: `1px solid ${palette.danger.main}`,
                backgroundColor: fade(palette.danger.main, palette.action.hoverOpacity),
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
            '&$disabled': {
                border: `1px solid ${palette.action.disabled}`,
            },
        },
        containedDanger: {
            color: palette.danger.contrastText,
            backgroundColor: palette.danger.main,
            '&:hover': {
                backgroundColor: palette.danger.dark,
                '@media (hover: none)': {
                    backgroundColor: palette.danger.main,
                },
            },
        },

    })
};

class Button extends React.PureComponent<ButtonProps> {

    render() {

        const { textTransform, classes, color, ...props } = this.props;
        let buttonColor: MuiPropTypes.Color = 'primary';
        let buttonClasses = {};
        switch (color) {

            case 'success':
                buttonClasses = {
                    textPrimary: classes.textSuccess,
                    outlinedPrimary: classes.outlinedSuccess,
                    containedPrimary: classes.containedSuccess,
                }
                break;

            case 'danger':
                buttonClasses = {
                    textPrimary: classes.textDanger,
                    outlinedPrimary: classes.outlinedDanger,
                    containedPrimary: classes.containedDanger,
                }
                break;

            case 'warning':
                buttonClasses = {
                    textPrimary: classes.textWarning,
                    outlinedPrimary: classes.outlinedWarning,
                    containedPrimary: classes.containedWarning,
                }
                break;

            case 'info':
                buttonClasses = {
                    textPrimary: classes.textInfo,
                    outlinedPrimary: classes.outlinedInfo,
                    containedPrimary: classes.containedInfo,
                }
                break;

            default:
                buttonColor = color;
        }

        switch (textTransform) {
            case 'lowercase':
                buttonClasses = { ...buttonClasses, root: classes.rootLowercase }
                break;
            case 'uppercase':
                buttonClasses = { ...buttonClasses, root: classes.rootUppercase }
                break;
            case 'capitalize':
                buttonClasses = { ...buttonClasses, root: classes.rootCapitalize }
                break;

            default:
                buttonClasses = { ...buttonClasses, root: classes.root }
        }

        buttonClasses = { ...buttonClasses, label: classes.label }

        const buttonProps: MuiButtonProps = {
            ...(props as MuiButtonProps),
            color: buttonColor,
            classes: buttonClasses
        };

        return <MuiButton {...buttonProps} >
            {this.props.children}
        </MuiButton>;
    }
}

export default withStyles(styles)(Button);
